"use client"

import { useMemo } from "react"
import { ChevronRight } from "lucide-react"
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useBreadcrumb } from "@/hooks/useBreadcrumb"

type SidebarItem = {
  title: string
  href: string
}

type SidebarItems = {
  [category: string]: SidebarItem[]
}

interface DynamicBreadcrumbProps {
  items: SidebarItems
}

export function DynamicBreadcrumb({ items }: DynamicBreadcrumbProps) {
  const breadcrumbs = useBreadcrumb(items)
  const visibleCrumbs = useMemo(() => {
    if (breadcrumbs.length <= 4) return breadcrumbs
    return [
      breadcrumbs[0],
      { href: "", title: "..." },
      ...breadcrumbs.slice(-2),
    ]
  }, [breadcrumbs])

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {visibleCrumbs.map((crumb, index) => (
          <BreadcrumbItem key={crumb.href || index}>
            {crumb.title === "..." ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1">
                    <BreadcrumbEllipsis className="h-4 w-4" />
                    <span className="sr-only">メニューを開く</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {breadcrumbs.slice(1, -2).map((item) => (
                    <DropdownMenuItem key={item.href}>
                      <BreadcrumbLink href={item.href}>
                        {item.title}
                      </BreadcrumbLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : index === visibleCrumbs.length - 1 ? (
              <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
            ) : (
              <BreadcrumbLink href={crumb.href}>{crumb.title}</BreadcrumbLink>
            )}
            {index < visibleCrumbs.length - 1 && (
              <BreadcrumbSeparator>
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
